import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Accessibility } from "lucide-react";
import { toast } from "sonner";
import { api, CATEGORIES, CITIES, FORMATS } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const INCLUSIVITY = ["Ramah Disabilitas", "Ramah Lansia"];
const EMPTY = { title: "", category: "", city: "Jakarta", format: "Offline", date: "", capacity: "", description: "", whatsapp: "" };

export default function CreateInitiativeModal({ open, onOpenChange }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [inclusivity, setInclusivity] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleInc = (x) => setInclusivity((p) => (p.includes(x) ? p.filter((i) => i !== x) : [...p, x]));

  const publish = async () => {
    if (!form.title.trim() || !form.category || !form.capacity) {
      toast.error("Lengkapi judul aksi, kategori, dan target kuota relawan.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/events", {
        ...form,
        capacity: parseInt(form.capacity, 10),
        inclusivity,
        organizer: user?.name || "Inisiatif Warga",
      });
      toast.success(`Inisiatif "${form.title}" berhasil dipublikasikan dan tampil teratas di Jelajahi Aksi!`);
      setForm(EMPTY);
      setInclusivity([]);
      onOpenChange(false);
      navigate("/jelajahi");
    } catch {
      toast.error("Gagal mempublikasikan inisiatif.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-initiative-modal" className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Plus className="w-5 h-5 text-emerald-600" /> Buat Inisiatif Baru
          </DialogTitle>
          <DialogDescription>
            Publikasikan kebutuhan relawan atas nama <span className="font-bold text-slate-700">{user?.name || "Inisiatif Warga"}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input data-testid="initiative-title-input" placeholder="Judul aksi (mis. Aksi Tanam 1000 Mangrove)"
            value={form.title} onChange={(e) => set("title", e.target.value)} className="rounded-xl" aria-label="Judul aksi" />
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="initiative-category-select" className="rounded-xl" aria-label="Kategori">
                <SelectValue placeholder="Kategori" />
              </SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.city} onValueChange={(v) => set("city", v)}>
              <SelectTrigger data-testid="initiative-city-select" className="rounded-xl" aria-label="Kota atau lokasi">
                <SelectValue placeholder="Kota/Lokasi" />
              </SelectTrigger>
              <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Select value={form.format} onValueChange={(v) => set("format", v)}>
              <SelectTrigger data-testid="initiative-format-select" className="rounded-xl" aria-label="Format kegiatan">
                <SelectValue placeholder="Format" />
              </SelectTrigger>
              <SelectContent>{FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="initiative-date-input" type="date" value={form.date}
              onChange={(e) => set("date", e.target.value)} className="rounded-xl" aria-label="Tanggal kegiatan" />
            <Input data-testid="initiative-capacity-input" type="number" min="1" placeholder="Kuota relawan"
              value={form.capacity} onChange={(e) => set("capacity", e.target.value)} className="rounded-xl" aria-label="Target kuota relawan" />
          </div>
          <Textarea data-testid="initiative-description-input" placeholder="Deskripsi singkat kegiatan dan kebutuhan relawan..."
            value={form.description} onChange={(e) => set("description", e.target.value)} className="rounded-xl min-h-[80px]" aria-label="Deskripsi singkat" />
          <Input data-testid="initiative-whatsapp-input" placeholder="Nomor WhatsApp PIC (mis. 0812xxxxxxx)"
            value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="rounded-xl" aria-label="Nomor WhatsApp PIC" />

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5">
            <p className="text-sm font-bold text-slate-700 flex items-center gap-1.5 mb-2">
              <Accessibility className="w-4 h-4 text-emerald-600" /> Opsi Inklusivitas
            </p>
            <div className="flex gap-5">
              {INCLUSIVITY.map((x) => (
                <label key={x} className="flex items-center gap-2 text-sm font-semibold text-slate-600 cursor-pointer">
                  <Checkbox
                    data-testid={`initiative-inclusivity-${INCLUSIVITY.indexOf(x)}`}
                    checked={inclusivity.includes(x)}
                    onCheckedChange={() => toggleInc(x)}
                    aria-label={x}
                  />
                  {x}
                </label>
              ))}
            </div>
          </div>

          <Button data-testid="initiative-publish-btn" onClick={publish} disabled={submitting}
            className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11">
            {submitting ? "Mempublikasikan..." : "Publikasikan Inisiatif"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
